import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  SubscriptionAction,
  SubscriptionStatus,
  SubscriptionType,
  TokenBillingType,
} from '@sc/graphql';
import { useUser } from '../providers/users';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import {
  GQL_MUTATION_UPDATE_SUBSCRIPTION,
  GQL_QUERY_LIST_PAYMENT_METHODS,
  GQL_QUERY_ME,
} from '../utility/queries';
import { AddPaymentMethodModal } from '../modals/AddPaymentMethodModal';

export interface SubscriptionActionOption {
  label: string;
  value: SubscriptionAction;
  danger?: boolean;
}

const planName = (type: SubscriptionType | null | undefined): string => {
  if (!type) return 'your';
  return `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
};

/**
 * Manage actions for the current self-serve subscription (pause / resume /
 * cancel-at-period-end / undo cancellation), shared by the billing tab's "Your
 * plan" panel and the upgrade screen's current-plan card.
 *
 * `manageOptions` is the menu the caller renders; `confirmAction` either runs
 * the action (resume) or opens a generic alert confirmation (pause / resume-plan
 * / cancel).
 */
export const useSubscriptionActions = () => {
  const { user } = useUser();
  const { setAlertModal, useApolloError } = useAlerts();
  const onError = useApolloError();

  const [updateSubscription, { loading: isUpdating }] = useMutation(
    GQL_MUTATION_UPDATE_SUBSCRIPTION,
    {
      onError,
      refetchQueries: [GQL_QUERY_ME],
      // Hold `updating` until the refreshed subscription status lands so the
      // caller's loader covers the whole status change, not just the mutation.
      awaitRefetchQueries: true,
    }
  );

  const subscription = user.company.subscription;

  // Only self-serve (Stripe-billed) subscriptions can be managed here; invoice-
  // billed enterprise plans are handled by the account manager.
  const isSelfServe =
    subscription?.tokenBillingType === TokenBillingType.subscription;

  const isPaused = subscription?.status === SubscriptionStatus.paused;
  const isPastDue = !!user.company.outstandingInvoice?.subscriptionUuid;


  const isExpiring = !!subscription?.expiresAt;

  // Resuming needs a card allocated to the monthly plan (e.g. it was removed
  // by a billing-details change) — without one, resume routes through the
  // add-card modal instead, and the resume completes once the card is saved.
  const { data: cardsData } = useQuery(GQL_QUERY_LIST_PAYMENT_METHODS);
  const hasMonthlyPlanCard = (cardsData?.listPaymentMethods ?? []).some(
    (card) => card.useForMonthlyPlan
  );

  const [addCardOpen, setAddCardOpen] = useState(false);

  const perform = (action: SubscriptionAction, successMessage: string) => {
    void updateSubscription({
      variables: { action },
      onCompleted: () => notify.success(successMessage),
    });
  };

  const openAddCard = () => setAddCardOpen(true);

  // Render this alongside the caller's UI: the add-card modal that saves the
  // card against the monthly plan and, when the plan is paused, resumes it.
  const addCardModal = (
    <AddPaymentMethodModal
      open={addCardOpen}
      onClose={() => setAddCardOpen(false)}
      useForMonthlyPlan
      onSuccess={() => {
        if (isPaused) perform(SubscriptionAction.resume, 'Subscription resumed.');
      }}
    />
  );

  const confirmAction = (action: SubscriptionAction) => {
    switch (action) {
      case SubscriptionAction.resume:
        // A resume with no card to bill goes through add-card first.
        if (!hasMonthlyPlanCard) {
          openAddCard();
          return;
        }

        // No confirmation — resuming is non-destructive.
        perform(action, 'Subscription resumed.');
        return;
      case SubscriptionAction.cancel:
        setAlertModal({
          open: true,
          title: 'Cancel subscription?',
          icon: 'critical',
          isDelete: true,
          submitText: 'Cancel plan',
          closeText: 'Go back',
          description: `Are you sure you want to cancel your “${planName(
            subscription?.type
          )}” plan? It won't renew, so you won't receive new usage. You can pause it and resume it later instead.`,
          onSubmit: () => {
            setAlertModal(undefined);

            perform(action, 'Your plan will cancel at the end of the period.');
          },
        });
        return;
      case SubscriptionAction.pause:
        setAlertModal({
          open: true,
          title: 'Pause subscription',
          description:
            'Pausing stops further charges and new usage until you resume. Existing usage stays available.',
          submitText: 'Pause',
          closeText: 'Cancel',
          icon: 'warning',
          onSubmit: () => {
            setAlertModal(undefined);

            perform(action, 'Subscription paused.');
          },
        });
        return;
      case SubscriptionAction.uncancel:
        setAlertModal({
          open: true,
          title: 'Resume plan',
          description:
            'This undoes your cancellation. Your plan continues as normal and renews at the next billing period.',
          submitText: 'Resume plan',
          closeText: 'Cancel',
          icon: 'success',
          onSubmit: () => {
            setAlertModal(undefined);

            perform(action, 'Cancellation reversed.');
          },
        });
        return;
    }
  };

  // Past-due is recovered via "Pay now" elsewhere, so no manage actions there.
  const manageOptions: SubscriptionActionOption[] =
    !subscription || !isSelfServe || isPastDue
      ? []
      : isExpiring
        ? [{ value: SubscriptionAction.uncancel, label: 'Resume plan' }]
        : [
            isPaused
              ? { value: SubscriptionAction.resume, label: 'Resume' }
              : { value: SubscriptionAction.pause, label: 'Pause' },
            {
              value: SubscriptionAction.cancel,
              label: 'Cancel plan',
              danger: true,
            },
          ];

  return {
    manageOptions,
    confirmAction,
    isPaused,
    isExpiring,
    isPastDue,
    isUpdating,
    hasMonthlyPlanCard,
    openAddCard,
    addCardModal,
  };
};
